import { types as callTypes, types } from '../../actions/backendCall';
import { types as asrTypes } from '../../actions/backendAsr';
import { CallMode } from '../../../constants/backend';

interface CallState {
    id: string | null;
    metaCallID: string | null;
    lastCallId: string | null;
    isInitialized: boolean;
    isStarted: boolean;
    isRecognitionStarted: boolean;
    sessionId: string | null;
    sessionDurationMsec: number;
    sessionStartOffsetMsec: number;
    sessionEndOffsetMsec: number;
    isOnHold: boolean;
    optInOn: boolean;
    isOptOut: boolean;
    removeCall: boolean;
    callMode: number;
}

export const initialState: CallState = {
    id: null,
    metaCallID: null,
    lastCallId: null,
    isInitialized: false,
    isStarted: false,
    isRecognitionStarted: false,
    sessionId: null,
    sessionDurationMsec: 0,
    sessionStartOffsetMsec: 0,
    sessionEndOffsetMsec: 0,
    isOnHold: false,
    optInOn: false,
    isOptOut: false,
    removeCall: false,
    callMode: CallMode.UNKNOWN
};

interface ReducerAction {
    type?: string;
    payload?: Record<string, unknown>;
}

interface CallPayload {
    sessionId?: string;
    duration?: number;
    sessionStartOffsetMsec?: number;
    id?: string;
    metaCallID?: string;
    sessionEndOffsetMsec?: number;
    mode?: number;
    resolvedMode?: number;
}

function toCallPayload(payload: Record<string, unknown>): CallPayload {
    return {
        sessionId: typeof payload.sessionId === 'string' ? payload.sessionId : undefined,
        duration: typeof payload.duration === 'number' ? payload.duration : undefined,
        sessionStartOffsetMsec: typeof payload.sessionStartOffsetMsec === 'number' ? payload.sessionStartOffsetMsec : undefined,
        id: typeof payload.id === 'string' ? payload.id : undefined,
        metaCallID: typeof payload.metaCallID === 'string' ? payload.metaCallID : undefined,
        sessionEndOffsetMsec: typeof payload.sessionEndOffsetMsec === 'number' ? payload.sessionEndOffsetMsec : undefined,
        mode: typeof payload.mode === 'number' ? payload.mode : undefined,
        resolvedMode: typeof payload.resolvedMode === 'number' ? payload.resolvedMode : undefined
    };
}

export default function callReducer(state = initialState, { type, payload = {} }: ReducerAction = {}) {
    const callPayload = toCallPayload(payload);

    switch (type) {
        case types.CALL_DETECTION_STARTED:
            return {
                ...state,
                isInitialized: true,
                sessionId: callPayload.sessionId || null
            };
        case asrTypes.SET_SESSION_DURATION:
            return {
                ...state,
                sessionDurationMsec: callPayload.duration || 0
            };
        case types.CALL_STARTED:
            return {
                ...state,
                isStarted: true,
                sessionStartOffsetMsec: callPayload.sessionStartOffsetMsec || 0,
                id: callPayload.id || null,
                metaCallID: callPayload.metaCallID || null,
                isOnHold: false,
                optInOn: false
            };
        case types.CALL_ENDED:
            return {
                ...state,
                isStarted: false,
                isRecognitionStarted: false,
                sessionEndOffsetMsec: callPayload.sessionEndOffsetMsec || 0,
                lastCallId: state.id,
                id: null,
                metaCallID: null,
                isOnHold: false
            };
        case types.RESET_SESSION_DURATION:
            return {
                ...state,
                sessionDurationMsec: 0,
                sessionStartOffsetMsec: 0,
                sessionEndOffsetMsec: 0
            };
        case types.CALL_HOLDED:
            return {
                ...state,
                isOnHold: true
            };
        case types.CALL_UNHOLDED:
            return {
                ...state,
                isOnHold: false
            };
        case types.OPT_IN:
            return {
                ...state,
                optInOn: true,
                isOptOut: false
            };
        case types.OPT_STOP:
            return {
                ...state,
                optInOn: false,
                isOptOut: false
            };
        case types.OPT_OUT:
            return {
                ...state,
                optInOn: false,
                isOptOut: true
            };
        case callTypes.RESET_CALL_MODE:
            return {
                ...state,
                optInOn: false,
                isOptOut: false,
                removeCall: false,
                callMode: CallMode.UNKNOWN
            };
        case types.CALL_SET_MODE: {
            if (state.callMode === CallMode.NONE) {
                return state;
            }

            const currentMode = callPayload.mode;
            const mode = currentMode === CallMode.DEFAULT ? (callPayload.resolvedMode ?? CallMode.DEFAULT) : currentMode;

            switch (mode) {
                case CallMode.BOTH:
                    return { ...state, callMode: CallMode.BOTH, optInOn: true, removeCall: false };
                case CallMode.AGENT:
                    return { ...state, callMode: CallMode.AGENT, optInOn: true, removeCall: false };
                case CallMode.CUSTOMER:
                    return { ...state, callMode: CallMode.CUSTOMER, optInOn: true, removeCall: false };
                case CallMode.NONE:
                    return { ...state, callMode: CallMode.NONE, optInOn: false, removeCall: true };
                default:
                    return { ...state, callMode: CallMode.UNKNOWN };
            }
        }
        default:
            return state;
    }
}
